import { createSelector } from 'reselect';
import {
	CUSTODIAN,
	PERSONAL_BROKERAGE,
	ROBO_PERSONAL_BROKERAGE,
	ROTH_IRA,
	TRADITIONAL_IRA,
} from 'stash/constants/accountTypes';
import { CLOSED_AASM_STATES } from 'stash/constants/mappings';

// Returns all accounts regardless of state
export const selectAllAccounts = (state) => {
	return state.entities.account || {};
};

// Returns only open accounts. Always use this unless you need to
// show closed accounts (i.e. tax docs)
export const selectAccounts = createSelector([selectAllAccounts], (accounts) =>
	Object.values(accounts).reduce((acc, a) => {
		return CLOSED_AASM_STATES[a.state] ? acc : { ...acc, [a.id]: a };
	}, {})
);

const selectActiveAccountId = (accounts, accountType = 'PERSONAL_BROKERAGE') => {
	const activeAccount = Object.values(accounts).find(
		(value) => value.type === accountType
	);
	return activeAccount?.id;
};

export const selectDefaultActiveAccountId = (state) => {
	const accounts = selectAccounts(state);
	return (
		selectActiveAccountId(accounts, PERSONAL_BROKERAGE) ||
		selectActiveAccountId(accounts, ROBO_PERSONAL_BROKERAGE) ||
		selectActiveAccountId(accounts, ROTH_IRA) ||
		selectActiveAccountId(accounts, TRADITIONAL_IRA) ||
		selectActiveAccountId(accounts, CUSTODIAN)
	);
};

export const selectAccountId = (state, accountType = 'PERSONAL_BROKERAGE') => {
	return state?.entities?.account_by_type?.[accountType];
};

export const selectAccountById = (state, accountId) => {
	return state?.entities?.account?.[accountId];
};

export const selectAccount = createSelector(
	[selectAllAccounts, selectAccountId],
	(accounts, accountId) => accounts[accountId]
);

export const selectDefaultAccountId = (state) => {
	return (
		selectAccountId(state, PERSONAL_BROKERAGE) ||
		selectAccountId(state, TRADITIONAL_IRA) ||
		selectAccountId(state, ROTH_IRA)
	);
};

export const selectMainAccount = createSelector(
	[selectAccounts, selectAccountId],
	(accounts, personalBrokerageId) =>
		accounts[personalBrokerageId] || Object.values(accounts)[0]
);
